import { FC, useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import moment from "moment";

import { useData } from "data/data-provider";
import DatePickerCustomHeaderOneMonth from "../DatePickerCustomHeaderOneMonth";
import DatePickerCustomHeaderTwoMonth from "../DatePickerCustomHeaderTwoMonth";
import { useClickOutside } from "./hooks/useClickOutside";
import {
  isOutsideOperationalHoursOn,
  windowForDate,
  clock,
} from "@1-now/booking-core";
import { useScreenSize } from "./hooks/useScreenSize";
import { calendarHeader } from "../CarPageB/calendarParts";

interface HoursViewProps {
  // Mirror the props the Dates tab calendar receives so the Hours tab
  // calendar renders identically (availability underlines, disabled
  // unavailable dates, month-change fetching). All optional so the
  // component degrades gracefully if a parent doesn't pass them.
  isDateSelectable?: (date: Date) => boolean;
  renderDayContents?: (day: number, date?: Date) => React.ReactNode;
  handleMonthChange?: (date: Date) => void;
  calendarViewDate?: Date | null;
  /** 1NOW-11176: Design B's quieter version (hairline chips, a plain calendar). */
  look?: "b";
}

const HoursView: FC<HoursViewProps> = ({
  isDateSelectable,
  renderDayContents,
  handleMonthChange,
  calendarViewDate,
  look,
}) => {
  const b = look === "b";
  const {
    startDate,
    endDate,
    startTime,
    endTime,
    setStartDate,
    setEndDate,
    setStartTime,
    setEndTime,
    setEndTimeTouched,
    ourCompany,
    pickupLocation,
  } = useData();

  const { isLargeScreen } = useScreenSize();
  const monthsToShow = isLargeScreen ? 2 : 1;


  const [isPickupTimeOpen, setIsPickupTimeOpen] = useState(false);
  const [isDropoffTimeOpen, setIsDropoffTimeOpen] = useState(false);

  const pickupTimeRef = useRef<HTMLDivElement>(null);
  const dropoffTimeRef = useRef<HTMLDivElement>(null);

  useClickOutside(pickupTimeRef, isPickupTimeOpen, () =>
    setIsPickupTimeOpen(false),
  );
  useClickOutside(dropoffTimeRef, isDropoffTimeOpen, () =>
    setIsDropoffTimeOpen(false),
  );

  // Seed defaults: today 10:00 → today 16:00 (6h block) on first open
  useEffect(() => {
    if (!startDate) {
      setStartDate(new Date());
      setStartTime("10:00");
    }
    if (!endDate) {
      setEndDate(new Date());
      setEndTimeTouched(true);
      setEndTime("16:00");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Compute current pickup → dropoff gap in hours (for Quick Select highlight)
  const startMoment = useMemo(
    () =>
      startDate && startTime
        ? moment(
            `${moment(startDate).format("YYYY-MM-DD")} ${startTime}`,
            "YYYY-MM-DD HH:mm",
          )
        : null,
    [startDate, startTime],
  );
  const endMoment = useMemo(
    () =>
      endDate && endTime
        ? moment(
            `${moment(endDate).format("YYYY-MM-DD")} ${endTime}`,
            "YYYY-MM-DD HH:mm",
          )
        : null,
    [endDate, endTime],
  );

  // Current pickup → dropoff gap in whole hours, used to mark the active
  // Quick Select chip with a green ✓.
  const gapHours = useMemo(() => {
    if (!startMoment || !endMoment) return null;
    const diff = endMoment.diff(startMoment, "hours", true);
    return diff > 0 ? diff : null;
  }, [startMoment, endMoment]);

  // Quick Select durations (hours) per spec.
  const QUICK_SELECT = [2, 4, 6, 8, 12, 24];

  // Quick Select: drop-off = pickup + N hours
  const applyQuickSelect = (hours: number) => {
    if (!startMoment) return;
    const newEnd = startMoment.clone().add(hours, "hours");
    setEndDate(newEnd.toDate());
    setEndTimeTouched(true);
    setEndTime(newEnd.format("HH:mm"));
  };

  // 1-hour increments only (per spec)
  const timeOptions = Array.from({ length: 24 }, (_, h) =>
    `${h.toString().padStart(2, "0")}:00`,
  );

  // Block times outside the hours the pickup location keeps ON THE SELECTED
  // DATE — hours are per weekday, so this can't be a single company-wide pair.
  const dayWindow = windowForDate(pickupLocation, startDate);
  const opStart = dayWindow?.start ?? null;
  const opEnd = dayWindow?.end ?? null;
  const isTimeOperational = (time: string): boolean =>
    !isOutsideOperationalHoursOn(time, pickupLocation, startDate);

  // The operator's clock (package clock): 12-hour unless they chose 24-hour,
  // as every other picker reads it. This view tested for "12_hour" instead,
  // so an operator who never set the field saw 24-hour times here only.
  const formatTime = (time: string | null | undefined) =>
    time ? clock(time, ourCompany) : "Time";

  const formatDateChip = (date: Date | null | undefined) => {
    if (!date) return "Until";
    return moment(date).format("dddd, MMM D");
  };

  // Human-readable "Available 7:00 AM – 7:00 PM" caption so the renter knows
  // why some time slots are disabled (1NOW-977 QA UX). Accepts both "HH:mm"
  // and "HH:mm:ss" since the API serializes TimeField as the latter.
  const formatOpTime = (t: string) => clock(t, ourCompany);
  const operatingHoursLabel =
    dayWindow === null
      ? "Closed on the selected date"
      : opStart && opEnd
        ? `Available ${formatOpTime(opStart)} – ${formatOpTime(opEnd)}`
        : null;

  // Range calendar handler.
  // react-datepicker's selectsRange fires [start, null] on the first click of
  // a fresh range and [start, end] on the second. We mirror both — including
  // null end — so the previous endDate doesn't get stranded behind a new
  // start date (which would invert the range).
  const onRangeChange = (dates: [Date | null, Date | null] | null) => {
    if (!dates) return;
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  return (
    <div className="px-2 pt-1 pb-2">
      {/* Pickup / Drop-off summary chip row */}
      <div className={b ? "mb-4 flex flex-wrap items-center gap-2" : "flex flex-wrap items-center justify-center gap-2 p-3 rounded-2xl bg-secondary-100/30 mb-5"}>
        {/* Pickup date (read-only — calendar below drives this) */}
        <div className={b ? "rounded-lg border border-text-color/20 px-3 py-2 text-sm font-medium text-text-color" : "px-4 py-2 bg-card-color rounded-lg text-sm font-medium text-text-color shadow-sm"}>
          {formatDateChip(startDate)}
        </div>

        {/* Pickup time */}
        <div className="relative" ref={pickupTimeRef}>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              setIsPickupTimeOpen((o) => !o);
              setIsDropoffTimeOpen(false);
            }}
            className={b ? "rounded-lg border border-text-color/20 px-3 py-2 text-sm font-medium text-text-color transition-colors hover:border-text-color/40" : "px-4 py-2 bg-card-color rounded-lg text-sm font-medium text-text-color shadow-sm hover:bg-secondary-100/40 transition-colors"}
          >
            {formatTime(startTime)}
          </button>
          {isPickupTimeOpen && (
            <div
              className="absolute left-0 top-full mt-2 z-50 rounded-xl shadow-xl ring-1 ring-black/5 bg-card-color p-2 max-h-64 overflow-y-auto w-28"
              onMouseDown={(e) => e.stopPropagation()}
            >
              {timeOptions.map((h) => {
                const operational = isTimeOperational(h);
                const isSelected = startTime === h;
                return (
                  <button
                    key={h}
                    type="button"
                    disabled={!operational}
                    title={!operational ? "Outside operating hours" : undefined}
                    onClick={(e) => {
                      e.preventDefault();
                      if (!operational) return;
                      setStartTime(h);
                      setIsPickupTimeOpen(false);
                    }}
                    className={`block w-full text-left px-3 py-1.5 rounded-md text-sm transition-colors ${
                      isSelected
                        ? b ? "bg-primary-brand/15 text-text-color font-semibold" : "bg-primary-brand text-on-primary font-semibold"
                        : !operational
                          ? "text-text-color/30 line-through cursor-not-allowed"
                          : "text-text-color hover:bg-secondary-100/40"
                    }`}
                  >
                    {formatTime(h)}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <span className="text-text-color/50 px-1 font-medium">–</span>

        {/* Drop-off time */}
        <div className="relative" ref={dropoffTimeRef}>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              setIsDropoffTimeOpen((o) => !o);
              setIsPickupTimeOpen(false);
            }}
            className={b ? "rounded-lg border border-text-color/20 px-3 py-2 text-sm font-medium text-text-color transition-colors hover:border-text-color/40" : "px-4 py-2 bg-card-color rounded-lg text-sm font-medium text-text-color shadow-sm hover:bg-secondary-100/40 transition-colors"}
          >
            {formatTime(endTime)}
          </button>
          {isDropoffTimeOpen && (
            <div
              className="absolute left-0 top-full mt-2 z-50 rounded-xl shadow-xl ring-1 ring-black/5 bg-card-color p-2 max-h-64 overflow-y-auto w-28"
              onMouseDown={(e) => e.stopPropagation()}
            >
              {timeOptions.map((h) => {
                const operational = isTimeOperational(h);
                const isSelected = endTime === h;
                return (
                <button
                  key={h}
                  type="button"
                  disabled={!operational}
                  title={!operational ? "Outside operating hours" : undefined}
                  onClick={(e) => {
                    e.preventDefault();
                    if (!operational) return;
                    setEndTimeTouched(true);
                    setEndTime(h);
                    setIsDropoffTimeOpen(false);
                  }}
                  className={`block w-full text-left px-3 py-1.5 rounded-md text-sm transition-colors ${
                    isSelected
                      ? b ? "bg-primary-brand/15 text-text-color font-semibold" : "bg-primary-brand text-on-primary font-semibold"
                      : !operational
                        ? "text-text-color/30 line-through cursor-not-allowed"
                        : "text-text-color hover:bg-secondary-100/40"
                  }`}
                >
                  {formatTime(h)}
                </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Drop-off date (read-only — calendar below drives this) */}
        <div className={b ? "rounded-lg border border-text-color/20 px-3 py-2 text-sm font-medium text-text-color" : "px-4 py-2 bg-card-color rounded-lg text-sm font-medium text-text-color shadow-sm"}>
          {formatDateChip(endDate)}
        </div>
      </div>

      {/* Operating-hours caption — explains why some time slots are disabled. */}
      {operatingHoursLabel && (
        <p className={b ? "mb-3 text-xs text-text-color/60" : "text-center text-xs text-text-color/60 -mt-3 mb-4"}>
          {operatingHoursLabel}
        </p>
      )}

      {/* Quick Select chips — auto-set drop-off = pickup + N hours.
          Active chip (matching the current gap) shows a green ✓. */}
      <div className={b ? "mb-4 flex flex-wrap items-center gap-2" : "flex flex-wrap items-center justify-center gap-2 mb-5"}>
        {QUICK_SELECT.map((hours) => {
          const isActive = gapHours === hours;
          return (
            <button
              key={hours}
              type="button"
              disabled={!startMoment}
              onClick={(e) => {
                e.preventDefault();
                applyQuickSelect(hours);
              }}
              className={`${b ? "rounded-lg border px-3 py-1.5" : "px-4 py-2 rounded-full"} text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                b
                  ? isActive
                    ? "border-primary-brand bg-primary-brand/10 text-text-color"
                    : "border-text-color/20 text-text-color hover:border-text-color/40"
                  : isActive
                    ? "bg-primary-brand text-on-primary"
                    : "bg-card-color text-text-color shadow-sm hover:bg-secondary-100/40"
              }`}
            >
              {isActive && !b && <span className="text-green-600 mr-1">✓</span>}
              {hours} {hours === 1 ? "hr" : "hrs"}
            </button>
          );
        })}
      </div>

      {/* Inline 2-month range calendar — same config as the Dates tab */}
      <div className={`hourly-range-calendar w-full mb-5${b ? " cpb-cal" : ""}`}>
        <DatePicker
          selected={startDate}
          onChange={(dates) =>
            onRangeChange(dates as [Date | null, Date | null])
          }
          startDate={startDate}
          endDate={endDate}
          selectsRange
          monthsShown={monthsToShow}
          showPopperArrow={false}
          inline
          minDate={new Date()}
          openToDate={calendarViewDate || startDate || new Date()}
          filterDate={isDateSelectable}
          renderCustomHeader={
            b
              ? calendarHeader(monthsToShow)
              : (p) => (
                  <>
                    <div className="block md:hidden">
                      <DatePickerCustomHeaderOneMonth {...p} />
                    </div>
                    <div className="hidden md:block">
                      <DatePickerCustomHeaderTwoMonth {...p} />
                    </div>
                  </>
                )
          }
          renderDayContents={renderDayContents}
          onMonthChange={handleMonthChange}
        />
      </div>
    </div>
  );
};

export default HoursView;
